import { ReactNode } from 'react';

import { Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';

import {
  createResourceLinkExtension,
  extractLastNameComponent,
  isResourceName,
} from '@spotify/confidence-core-react';

import { confidenceAdminPlugin } from '../plugin';
import { entityRouteRef, factTableRouteRef } from '../routes';

interface EntityResourceLinkProps {
  name: string;
  text: ReactNode;
  className?: string;
}

const isEntityResourceName = (name: string) =>
  isResourceName(name, 'entities') &&
  !name.slice('entities/'.length).includes('/');

const EntityResourceLink = ({
  name,
  text,
  className,
}: EntityResourceLinkProps) => {
  const routeRef = useRouteRef(entityRouteRef);
  const id = extractLastNameComponent(name);

  if (!routeRef || !id) {
    return <>{text}</>;
  }

  const to = routeRef({ id } as Parameters<NonNullable<typeof routeRef>>[0]);

  return (
    <Link to={to} className={className} data-track-source-anchor="0561bf3c6a;entity-resource-link">
      {text}
    </Link>
  );
};

interface FactTableResourceLinkProps {
  name: string;
  text: ReactNode;
  className?: string;
}

const isFactTableResourceName = (name: string) =>
  name.startsWith('factTables/') &&
  !name.slice('factTables/'.length).includes('/');

const FactTableResourceLink = ({
  name,
  text,
  className,
}: FactTableResourceLinkProps) => {
  const routeRef = useRouteRef(factTableRouteRef);
  const id = extractLastNameComponent(name);

  if (!routeRef || !id) {
    return <>{text}</>;
  }

  const to = routeRef({ id } as Parameters<NonNullable<typeof routeRef>>[0]);

  return (
    <Link to={to} className={className} data-track-source-anchor="26e5db12a0;fact-table-resource-link">
      {text}
    </Link>
  );
};

export const EntityResourceLinkExtension = confidenceAdminPlugin.provide(
  createResourceLinkExtension({
    id: 'entity-resource-link',
    predicate: ({ name }) => isEntityResourceName(name),
    icon: 'entity',
    render: ({ name, text, className }) => (
      <EntityResourceLink name={name} text={text} className={className}  data-track-source-anchor="d226dcb677;resource-link-extension-entity-resource-link"/>
    ),
  }),
);

export const FactTableResourceLinkExtension = confidenceAdminPlugin.provide(
  createResourceLinkExtension({
    id: 'fact-table-resource-link',
    predicate: ({ name }) => isFactTableResourceName(name),
    icon: 'factTable',
    render: ({ name, text, className }) => (
      <FactTableResourceLink name={name} text={text} className={className}  data-track-source-anchor="5d773ac2b4;resource-link-extension-fact-table-resource-link"/>
    ),
  }),
);
